import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { FunctionComponent, useMemo, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './KanbanColumnSelection.module.scss';

interface KanbanColumnSelectionProps {
    appointments: Array<AppointmentListItem>;
    visibleAppointments: Array<AppointmentListItem>;
    selectedIds: Set<number>;
    statusId: number | null;
    onToggleSelectAll: (checked: boolean, appointmentIds: Array<number>) => void;
    onSelectStatus: (statusId: number | null) => void;
    disabled?: boolean;
    className?: string;
}

export const KanbanColumnSelection: FunctionComponent<KanbanColumnSelectionProps> = props => {
    const [open, setOpen] = useState(false);

    const visibleIds = props.visibleAppointments.map(app => app.appointmentId);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => props.selectedIds.has(id));

    const statuses = useMemo(() => {
        const options: Array<{ id: number; name: string }> = [];
        props.appointments.forEach(app => {
            if (!options.some(option => option.id === app.status.id)) {
                options.push({ id: app.status.id, name: app.status.name });
            }
        });
        return options.sort((a, b) => a.name.localeCompare(b.name));
    }, [props.appointments]);

    if (props.appointments.length === 0) {
        return null;
    }

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <div
                className={clsx(styles.root, props.className)}
                onClick={event => event.stopPropagation()}
            >
                <div className={clsx(props.disabled && styles.checkboxDisabled)}>
                    <Checkbox
                        checked={props.disabled ? false : allSelected}
                        onChange={props.disabled ? undefined : checked => props.onToggleSelectAll(checked, visibleIds)}
                    />
                </div>
                <button
                    type="button"
                    className={clsx(styles.trigger, props.disabled && styles.triggerDisabled)}
                    aria-label="Filter by status"
                    aria-expanded={open}
                    disabled={props.disabled}
                    onClick={() => setOpen(prev => !prev)}
                >
                    <FontAwesomeIcon icon={faAngleDown} />
                </button>
                {open && (
                    <div className={styles.menu}>
                        <button
                            type="button"
                            className={clsx(styles.option, { [styles.optionSelected]: props.statusId == null })}
                            onClick={() => {
                                props.onSelectStatus(null);
                                setOpen(false);
                            }}
                        >
                            All
                        </button>
                        {statuses.map(status => (
                            <button
                                key={status.id}
                                type="button"
                                className={clsx(styles.option, {
                                    [styles.optionSelected]: props.statusId === status.id
                                })}
                                onClick={() => {
                                    props.onSelectStatus(status.id);
                                    setOpen(false);
                                }}
                            >
                                {status.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};

export default KanbanColumnSelection;
